import { Box, Divider, Grid, Stack } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import {
  DataGridLoading,
  EnhancedFilter,
  FilterChipList,
  FilterSearchContainer,
  FormContent,
  FormDrawer,
  FormFooter,
  FormHeader,
  LinkButton,
  RouterLinkBehavior,
  StateMessage,
} from "Common/Components";
import { SearchBox } from "Common/Components/SearchBox";
import { NoDataFoundImage } from "Common/Assets";
import { useNotificationDrawer } from "./Hooks";
import { NotificationDetailPopup, NotificationsEmptyState, VirtuosoItemContent, VirtuosoFooter } from "./Components";
import { useTheme } from "@mui/material/styles";
import { useCallback, useMemo } from "react";
import type { INotification } from "Common/Interfaces";

interface INotificationDrawer {
  open: boolean;
  onClose: () => void;
  sessionStartTime: string;
  pendingSignalRItems: INotification[];
  onPendingItemsConsumed: () => void;
  onDismissed: () => void;
  onMarkedUnread: () => void;
  onAllDismissed: () => void;
}

const VirtuosoFooterWrapper = ({ context }: { context?: { isFetching: boolean } }) => (
  <VirtuosoFooter isFetching={context?.isFetching ?? false} />
);

const NOTIFICATION_APP_URL = import.meta.env.VITE_NOTIFICATION_APP_URL;

export const NotificationDrawer = (props: INotificationDrawer) => {
  const {
    open,
    onClose,
    sessionStartTime,
    pendingSignalRItems,
    onPendingItemsConsumed,
    onDismissed,
    onMarkedUnread,
    onAllDismissed,
  } = props;
  const theme = useTheme();

  const {
    allItems,
    isFetching,
    isDataLoaded,
    search,
    isFiltering,
    selectedNotification,
    isDetailOpen,
    filterState,
    newlyArrivedIds,
    exitingIds,
    handleRowExited,
    handleSearch,
    handleClearSearch,
    handleUpdateFilterState,
    handleNotificationClick,
    handleDismiss,
    handleDismissAll,
    handleLoadMore,
    handleClose,
    handleDetailClose,
    formatRelativeTime,
  } = useNotificationDrawer({
    open,
    onClose,
    sessionStartTime,
    pendingSignalRItems,
    onPendingItemsConsumed,
    onDismissed,
    onMarkedUnread,
    onAllDismissed,
  });

  const itemContent = useCallback(
    (index: number) => (
      <VirtuosoItemContent
        index={index}
        allItems={allItems}
        onNotificationClick={handleNotificationClick}
        onDismiss={handleDismiss}
        formatRelativeTime={formatRelativeTime}
        newlyArrivedIds={newlyArrivedIds}
        exitingIds={exitingIds}
        onRowExited={handleRowExited}
      />
    ),
    [allItems, handleNotificationClick, handleDismiss, formatRelativeTime, newlyArrivedIds, exitingIds, handleRowExited],
  );

  const virtuosoComponents = useMemo(
    () => ({
      Footer: VirtuosoFooterWrapper,
    }),
    [],
  );

  const showEmptyState = isDataLoaded && allItems.length === 0 && !isFetching && !(search || isFiltering);

  const renderMainContent = () => {
    if (allItems.length === 0 && (!isDataLoaded || isFetching)) {
      return (
        <DataGridLoading isLoading>
          <Box sx={{ flex: 1 }} />
        </DataGridLoading>
      );
    }

    if (allItems.length === 0) {
      if (!showEmptyState) {
        return (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <StateMessage
              image={<NoDataFoundImage />}
              smallSize
              message="No Data to Display"
              secondaryMessage="Try adjusting your search or filter."
              sx={{ textAlign: "center" }}
            />
          </Box>
        );
      }

      return <NotificationsEmptyState />;
    }

    return (
      <Virtuoso
        style={{ flex: 1 }}
        totalCount={allItems.length}
        computeItemKey={(index) => allItems[index]?.id ?? index}
        endReached={handleLoadMore}
        itemContent={itemContent}
        components={virtuosoComponents}
      />
    );
  };

  return (
    <FormDrawer open={open}>
      <FormHeader title="Notifications" handleClose={handleClose} />

      <FormContent>
        {!showEmptyState && (
          <>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12 }}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <LinkButton
                    component={RouterLinkBehavior}
                    to={`${NOTIFICATION_APP_URL}/notifications`}
                    target="_blank"
                    onClick={handleClose}
                  >
                    View all notifications &rarr;
                  </LinkButton>

                  <LinkButton onClick={handleDismissAll}>Dismiss all</LinkButton>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ mb: theme.spacing(1.5) }} />
              </Grid>
            </Grid>

            {/* Search + Filter */}
            <Grid container sx={{ py: 1 }}>
              <Grid size={{ xs: 12 }}>
                <FilterSearchContainer>
                  <SearchBox
                    placeholder="Search by Message Topic or Message Body"
                    handleSearch={handleSearch}
                    handleClear={handleClearSearch}
                    size="small"
                  />
                  <EnhancedFilter filterState={filterState} handleUpdateFilterState={handleUpdateFilterState} />
                </FilterSearchContainer>
              </Grid>
            </Grid>

            {/* Filter chips */}
            <Grid container sx={{ pb: 2 }}>
              <Grid size={{ xs: 12 }}>
                <FilterChipList filterState={filterState} handleFilterState={handleUpdateFilterState} />
              </Grid>
            </Grid>
          </>
        )}

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>{renderMainContent()}</Box>
      </FormContent>

      <FormFooter buttons={[{ label: "Close", variant: "contained", onClick: handleClose }]} />

      <NotificationDetailPopup open={isDetailOpen} notification={selectedNotification} handleClose={handleDetailClose} />
    </FormDrawer>
  );
};
